import { DateTime } from 'luxon';

interface DayInterval {
    start: DateTime;
    end: DateTime;
}

interface DayTimes {
    sunrise: DateTime;
    sunset: DateTime;
}

export class GulikaCalculator {
    /**
     * Calculates Gulika interval for a given date and sunrise/sunset times
     * @param date The date to calculate for
     * @param dayTimes Object containing sunrise and sunset times
     * @returns DayInterval The Gulika interval
     */
    calculateGulikaInterval(date: DateTime, dayTimes: DayTimes): DayInterval {
        const { sunrise, sunset } = dayTimes;

        // Skip date validation - we'll use the sunrise/sunset times as provided
        // This allows for API responses that might return times in UTC or other timezones

        // Calculate day duration
        const dayDuration = sunset.diff(sunrise);

        // Divide day into 8 parts
        const onePart = dayDuration.as('milliseconds') / 8;

        // Get Saturn's portion based on day of week
        const saturnPortion = this.getSaturnPortion(date.weekday);

        const intervalStart = sunrise.plus({ milliseconds: onePart * saturnPortion });
        const intervalEnd = intervalStart.plus({ milliseconds: onePart });

        return {
            start: intervalStart,
            end: intervalEnd
        };
    }

    /**
     * Calculates Rahu Kala interval for a given date and sunrise/sunset times
     * @param date The date to calculate for
     * @param dayTimes Object containing sunrise and sunset times
     * @returns DayInterval The Rahu Kala interval
     */
    calculateRahuInterval(date: DateTime, dayTimes: DayTimes): DayInterval {
        const { sunrise, sunset } = dayTimes;

        // Skip date validation - we'll use the sunrise/sunset times as provided
        // This allows for API responses that might return times in UTC or other timezones

        // Calculate day duration
        const dayDuration = sunset.diff(sunrise);

        // Divide day into 8 parts
        const onePart = dayDuration.as('milliseconds') / 8;

        // Get Rahu's portion based on day of week
        const rahuPortion = this.getRahuPortion(date.weekday);

        const intervalStart = sunrise.plus({ milliseconds: onePart * rahuPortion });
        const intervalEnd = intervalStart.plus({ milliseconds: onePart });

        return {
            start: intervalStart,
            end: intervalEnd
        };
    }

    /**
     * Calculates Yamagandam interval for a given date and sunrise/sunset times
     * @param date The date to calculate for
     * @param dayTimes Object containing sunrise and sunset times
     * @returns DayInterval The Yamagandam interval
     */
    calculateYamagandamInterval(date: DateTime, dayTimes: DayTimes): DayInterval {
        const { sunrise, sunset } = dayTimes;

        // Skip date validation - we'll use the sunrise/sunset times as provided
        // This allows for API responses that might return times in UTC or other timezones

        // Calculate day duration
        const dayDuration = sunset.diff(sunrise);

        // Divide day into 8 parts
        const onePart = dayDuration.as('milliseconds') / 8;

        // Get Yamagandam's portion based on day of week
        const yamagandamPortion = this.getYamagandamPortion(date.weekday);

        const intervalStart = sunrise.plus({ milliseconds: onePart * yamagandamPortion });
        const intervalEnd = intervalStart.plus({ milliseconds: onePart });

        return {
            start: intervalStart,
            end: intervalEnd
        };
    }

    /**
     * Gets Saturn's portion number based on day of week
     * @param weekday Number from 1-7 (Monday = 1)
     * @returns number The portion number for Saturn
     */
    private getSaturnPortion(weekday: number): number {
        // Saturn's portion varies by weekday
        const saturnPortions: Record<number, number> = {
            1: 6, // Monday
            2: 5, // Tuesday
            3: 4, // Wednesday
            4: 3, // Thursday
            5: 2, // Friday
            6: 1, // Saturday
            7: 7, // Sunday
        };
        return saturnPortions[weekday] - 1;
    }

    /**
     * Gets Rahu's portion number based on day of week
     * @param weekday Number from 1-7 (Monday = 1)
     * @returns number The portion number for Rahu
     */
    private getRahuPortion(weekday: number): number {
        // Rahu's portion varies by weekday
        const rahuPortions: Record<number, number> = {
            1: 2, // Monday: 2nd part
            2: 7, // Tuesday: 7th part
            3: 5, // Wednesday: 5th part
            4: 6, // Thursday: 6th part
            5: 4, // Friday: 4th part
            6: 3, // Saturday: 3rd part
            7: 8, // Sunday: 8th part
        };
        return rahuPortions[weekday] - 1;
    }

    /**
     * Gets Yamagandam's portion number based on day of week
     * @param weekday Number from 1-7 (Monday = 1)
     * @returns number The portion number for Yamagandam
     */
    private getYamagandamPortion(weekday: number): number {
        // Yamagandam's portion varies by weekday
        const yamagandamPortions: Record<number, number> = {
            1: 4, // Monday: 4th part (about 10:30 AM - 12:00 PM)
            2: 3, // Tuesday: 3rd part (about 9:00 AM - 10:30 AM)
            3: 2, // Wednesday: 2nd part (about 7:30 AM - 9:00 AM)
            4: 1, // Thursday: 1st part (about 6:00 AM - 7:30 AM)
            5: 8, // Friday: 8th part (about 3:00 PM - 4:30 PM)
            6: 7, // Saturday: 7th part (about 1:30 PM - 3:00 PM)
            7: 5, // Sunday: 5th part (about 12:00 PM - 1:30 PM)
        };
        return yamagandamPortions[weekday] - 1;
    }

    /**
     * Checks if two DateTime objects are on the same day
     * @param date1 First DateTime
     * @param date2 Second DateTime
     * @returns boolean
     */
    private areSameDay(date1: DateTime, date2: DateTime): boolean {
        return date1.hasSame(date2, 'day');
    }

    /**
     * Calculates Gulika's longitude
     * @param sunLongitude The sun's longitude in degrees
     * @returns number Gulika's longitude in degrees
     */
    calculateGulikaLongitude(sunLongitude: number): number {
        // Add 4 signs (4 * 30 = 120 degrees) plus 13 degrees and 20 minutes
        const addition = 120 + 13 + (20 / 60);
        let gulikaLongitude = sunLongitude + addition;

        // Normalize to 0-360 degrees
        while (gulikaLongitude >= 360) {
            gulikaLongitude -= 360;
        }

        return gulikaLongitude;
    }

    /**
     * Gets the zodiac sign name for a given longitude
     * @param longitude The longitude in degrees
     * @returns string The zodiac sign name
     */
    getZodiacSign(longitude: number): string {
        const signs = [
            'Aries', 'Taurus', 'Gemini', 'Cancer',
            'Leo', 'Virgo', 'Libra', 'Scorpio',
            'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
        ];

        const signIndex = Math.floor(longitude / 30);
        return signs[signIndex];
    }
}

// Example usage:
/*
import { DateTime } from 'luxon';

const calculator = new GulikaCalculator();

// Example with custom sunrise/sunset times
const date = DateTime.local();
const dayTimes = {
  sunrise: date.set({ hour: 6, minute: 30 }), // Example: sunrise at 6:30 AM
  sunset: date.set({ hour: 18, minute: 45 })  // Example: sunset at 6:45 PM
};

const interval = calculator.calculateGulikaInterval(date, dayTimes);
console.log(`Gulika interval: ${interval.start.toFormat('HH:mm')} - ${interval.end.toFormat('HH:mm')}`);

// Calculate Gulika's longitude
const sunLongitude = 45; // Example: Sun in Taurus
const gulikaLongitude = calculator.calculateGulikaLongitude(sunLongitude);
console.log(`Gulika longitude: ${gulikaLongitude}° (${calculator.getZodiacSign(gulikaLongitude)})`);
*/